import { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data) {
        toast.success("Message Sent Successfully!", {
          description: "We'll get back to you within 24 hours.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("Failed to send message", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32" style={{ background: 'var(--bg-dark)' }}>
      {/* Background Accents */}
      <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-[#3B82F6]/5 rounded-full blur-[150px]"></div>
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-[#8B5CF6]/5 rounded-full blur-[150px]"></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/10 border border-[#F97316]/30 rounded-full mb-6">
            <span className="text-[#F97316] text-sm font-semibold">Get In Touch</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Let's Build Something{' '}
            <span className="bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent">
              Amazing
            </span>
          </h2>
          
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Ready to transform your business? Reach out and let's discuss how we can help you achieve your goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8">Contact Information</h3>
            
            <div className="space-y-6 mb-12">
              {/* Phone */}
              <div className="flex items-start gap-4 group">
                <div className="bg-[#3B82F6]/10 p-4 rounded-xl border border-[#3B82F6]/30 transition-all duration-400 group-hover:bg-[#3B82F6]/20 group-hover:scale-110">
                  <Phone size={24} className="text-[#3B82F6]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Phone</div>
                  <a href={`tel:${contactInfo.phone}`} className="text-white text-lg font-medium hover:text-[#3B82F6] transition-colors duration-300">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 group">
                <div className="bg-[#8B5CF6]/10 p-4 rounded-xl border border-[#8B5CF6]/30 transition-all duration-400 group-hover:bg-[#8B5CF6]/20 group-hover:scale-110">
                  <Mail size={24} className="text-[#8B5CF6]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Email</div>
                  <a href={`mailto:${contactInfo.email}`} className="text-white text-lg font-medium hover:text-[#8B5CF6] transition-colors duration-300">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4 group">
                <div className="bg-[#F97316]/10 p-4 rounded-xl border border-[#F97316]/30 transition-all duration-400 group-hover:bg-[#F97316]/20 group-hover:scale-110">
                  <MapPin size={24} className="text-[#F97316]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Address</div>
                  <div className="text-white text-lg font-medium">
                    {contactInfo.address}
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="border border-white/10 overflow-hidden h-[300px] rounded-2xl">
              <iframe
                title="CLOUD IT Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2916.123456789012!2d-78.72!3d42.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDU0JzM2LjAiTiA3OMKwNDMnMTIuMCJX!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#12121A]/80 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-white mb-6">Send Us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-white/70 mb-2 text-sm font-medium">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all duration-300"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-white/70 mb-2 text-sm font-medium">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6]/50 transition-all duration-300"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-white/70 mb-2 text-sm font-medium">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="contact-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316]/50 transition-all duration-300"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-white/70 mb-2 text-sm font-medium">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all duration-300 resize-none"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={isSubmitting}
                className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#3B82F6]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
